import { useState } from "react";
import { DateTime } from "luxon";
import { useStore } from "../../../stores";
import { ChatActivityLogType, parseActivityLog } from "../../../models/Chat";
import { Message as MessageModel } from "../../../models";

type GroupedActivityLogsProps = {
  messages: MessageModel[];
};

type SingleActivityLogProps = {
  message: MessageModel;
  time: string;
  label?: string;
};

export const GroupedActivityLogs = ({ messages }: GroupedActivityLogsProps) => {
  const { allUsers } = useStore("users");
  const [isExpanded, setIsExpanded] = useState(false);

  if (messages.length === 0) return null;

  const firstMessage = messages[0];
  const lastMessage = messages[messages.length - 1];

  const firstTime = DateTime.fromJSDate(firstMessage.createdAt);
  const lastTime = DateTime.fromJSDate(lastMessage.createdAt);

  const timeRange = firstTime.hasSame(lastTime, "day")
    ? `${firstTime.toFormat("t")} - ${lastTime.toFormat("t")}`
    : `${firstTime.toLocaleString({
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })} - ${lastTime.toLocaleString({
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })}`;

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div
      className="is-flex py-3 is-relative is-justify-content-center is-align-items-center is-flex-direction-column"
      style={{ opacity: 0.65 }}
    >
      <div
        className="is-clickable is-flex is-align-items-center px-3 py-2 has-background-light has-rounded-corners"
        onClick={handleToggle}
        style={{
          cursor: "pointer",
          transition: "all 0.2s ease",
          border: "1px solid #e8e8e8",
        }}
      >
        <span className="icon mr-2" style={{ minWidth: "20px" }}>
          <i
            className={`fas fa-chevron-${isExpanded ? "up" : "down"}`}
            style={{ fontSize: "12px" }}
          ></i>
        </span>
        <span style={{ fontSize: "13px", fontWeight: "500" }}>
          {messages.length} activity log{messages.length > 1 ? "s" : ""}
        </span>
        <small className="ml-3" style={{ color: "#666", fontSize: "11px" }}>
          {timeRange}
        </small>
      </div>

      {isExpanded && (
        <div
          className="mt-3 px-3"
          style={{
            width: "100%",
            maxWidth: "90%",
            background: "rgba(248, 249, 250, 0.8)",
            borderRadius: "8px",
            border: "1px solid #e9ecef",
          }}
        >
          {messages.map((message, index) => (
            <div
              key={message.id}
              className="py-2"
              style={{
                borderBottom:
                  index < messages.length - 1 ? "1px solid #e9ecef" : "none",
                opacity: 0.9,
              }}
            >
              <div className="is-flex is-flex-direction-column">
                <div
                  style={{
                    fontSize: "12px",
                    lineHeight: "1.4",
                    color:
                      message.meta?.type === ChatActivityLogType.ERROR
                        ? "#ff2e2b"
                        : "#495057",
                    wordBreak: "break-word",
                    marginBottom: "4px",
                  }}
                >
                  {parseActivityLog(message.meta as any, allUsers)}
                </div>
                <small
                  style={{
                    color: "#868e96",
                    fontSize: "10px",
                    alignSelf: "flex-end",
                  }}
                >
                  {DateTime.fromJSDate(message.createdAt).hasSame(
                    DateTime.utc(),
                    "day"
                  )
                    ? DateTime.fromJSDate(message.createdAt).toFormat("t")
                    : DateTime.fromJSDate(message.createdAt).toLocaleString({
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                </small>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const SingleActivityLog = ({
  message,
  time,
  label,
}: SingleActivityLogProps) => {
  const { allUsers } = useStore("users");

  return (
    <div
      className="is-flex py-3 is-relative is-justify-content-center is-align-items-center"
      style={{ opacity: 0.7 }}
    >
      <div
        className="px-3 py-2 has-background-light has-rounded-corners"
        style={{
          border: "1px solid #e8e8e8",
          maxWidth: "85%",
        }}
      >
        <div className="is-flex is-flex-direction-column">
          <span
            style={{
              fontSize: "12px",
              lineHeight: "1.4",
              color:
                message.meta?.type === ChatActivityLogType.ERROR
                  ? "#ff2e2b"
                  : "#495057",
              wordBreak: "break-word",
              marginBottom: "4px",
              textAlign: "center",
            }}
          >
            {label ?? parseActivityLog(message.meta as any, allUsers)}
          </span>
          <small
            style={{
              color: "#868e96",
              fontSize: "10px",
              alignSelf: "center",
            }}
          >
            {time}
          </small>
        </div>
      </div>
    </div>
  );
};
